import React, { useEffect, useState } from "react";
import { X } from "lucide-react";
import { getReferralOfferedSlots } from "../APIs/referralApi";
import BookingModal from "./BookingModal";

const ZONES = [
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "UTC",
];

function formatTime(iso, zone) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      timeStyle: "short",
      timeZone: zone,
    }).format(new Date(iso));
  } catch {
    return iso ?? "";
  }
}

function formatDate(iso, zone) {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeZone: zone,
    }).format(new Date(iso));
  } catch {
    return iso ?? "";
  }
}

function durationMinutes(start, end) {
  try {
    return Math.round((new Date(end) - new Date(start)) / 60000);
  } catch {
    return null;
  }
}

export default function ReferralSlotSelectionModal({
  referralId,
  onClose,
  providerName,
  specialty,
}) {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [zone, setZone] = useState("America/New_York");
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");
  const [bookingSlot, setBookingSlot] = useState(null);

  useEffect(() => {
    getReferralOfferedSlots(referralId)
      .then((data) => setSlots(Array.isArray(data) ? data : []))
      .catch(() => setError("Failed to load available slots."))
      .finally(() => setLoading(false));
  }, [referralId]);

  const handleConfirm = async () => {
    if (!selected) return;
    setError("");
    const fullSlot = slots.find((s) => s.slotStartUtc === selected);
    if (!fullSlot) {
      setError("The selected slot is no longer available.");
      return;
    }
    setBookingSlot(fullSlot);
  };

  // Group slots by date
  const grouped = slots.reduce((acc, slot) => {
    const dateKey = formatDate(slot.slotStartUtc, zone);
    if (!acc[dateKey]) acc[dateKey] = [];
    acc[dateKey].push(slot);
    return acc;
  }, {});

  if (bookingSlot) {
    return (
      <BookingModal
        open
        onClose={() => setBookingSlot(null)}
        providerId={bookingSlot.specialistProviderId}
        providerName={providerName || "Specialist"}
        slotZdt={bookingSlot.slotStartUtc}
        specialty={specialty || ""}
        facility=""
        referralId={referralId}
      />
    );
  }

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4 max-h-[90vh] overflow-y-auto">
          <div className="flex items-center justify-between p-5 border-b">
            <h2 className="text-lg font-semibold">
              Pick Your Appointment Time
            </h2>
            <button onClick={onClose} className="p-1 hover:bg-gray-100 rounded">
              <X size={20} />
            </button>
          </div>

          <div className="p-5 space-y-4">
            {/* Timezone selector */}
            <div>
              <label className="block text-xs font-medium text-gray-500 mb-1">
                Timezone
              </label>
              <select
                className="border rounded px-3 py-1.5 text-sm"
                value={zone}
                onChange={(e) => setZone(e.target.value)}
              >
                {ZONES.map((z) => (
                  <option key={z} value={z}>
                    {z}
                  </option>
                ))}
              </select>
            </div>

            {loading && (
              <p className="text-sm text-gray-500 text-center py-4">
                Loading available slots...
              </p>
            )}
            {error && <p className="text-sm text-red-600">{error}</p>}

            {!loading && slots.length === 0 && !error && (
              <p className="text-sm text-gray-500 text-center py-4">
                No slots available at this time.
              </p>
            )}

            {Object.entries(grouped).map(([dateLabel, dateSlots]) => (
              <div key={dateLabel}>
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">
                  {dateLabel}
                </p>
                <div className="flex flex-wrap gap-2">
                  {dateSlots.map((slot) => {
                    const isSelected = selected === slot.slotStartUtc;
                    const dur = durationMinutes(
                      slot.slotStartUtc,
                      slot.slotEndUtc,
                    );
                    return (
                      <button
                        key={slot.slotStartUtc}
                        onClick={() => setSelected(slot.slotStartUtc)}
                        title={dur ? `${dur} min` : undefined}
                        className={`px-3 py-1.5 rounded-full text-sm font-medium transition ${
                          isSelected
                            ? "bg-blue-600 text-white"
                            : "bg-blue-50 text-blue-700 border border-blue-200 hover:bg-blue-100"
                        }`}
                      >
                        {formatTime(slot.slotStartUtc, zone)}
                      </button>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 px-5 py-4 border-t bg-gray-50">
            <button
              onClick={onClose}
              className="px-4 py-2 text-sm rounded border"
            >
              Cancel
            </button>
            <button
              disabled={!selected}
              onClick={handleConfirm}
              className="px-4 py-2 text-sm rounded bg-blue-600 text-white disabled:opacity-40"
            >
              Continue
            </button>
          </div>
        </div>
      </div>
    </>
  );
}
